<template>
  <div
    ref="dropdownAnchor"
    class="group relative flex flex-nowrap items-center justify-center"
    @mouseenter="open"
    @mouseleave="close"
  >
    <CustomButton
      class="flex flex-nowrap items-center justify-center w-10 lg:w-auto"
      type="raw"
      data-testid="login-account"
      :aria-label="$t('My Account')"
      @click="() => handleAccountClick()"
    >
      <span class="iconify svg--account text-base text-EXPONDO-primary text-xl lg:mr-3" />
      <div class="text-left hidden whitespace-nowrap text-EXPONDO-primary lg:block">
        <p class="font-bold">
          {{ $t('My Account') }}
        </p>
        <MyAccountLoyaltyLevelLabel v-if="canShowLoyaltyLevelLabel" />
        <p
          v-else
          class="text-xs font-normal"
        >
          {{ $t('Login/Register') }}
        </p>
      </div>
    </CustomButton>
    <div
      v-if="emailAddress"
      :class="[
        'max-lg:hidden transition-all duration-[400ms] absolute',
        'top-full z-40',
        isOpen ? 'visible opacity-100' : 'invisible opacity-0 pointer-events-none'
      ]"
      :style="{ left: `${panelLeftOffset}px`, right: `${panelRightOffset}px` }"
      data-testid="myAccountDropdown"
    >
      <div class="fixed top-0 left-0 bg-EXPONDO-black/25 w-full h-full pointer-events-none" />
      <div class="relative bg-EXPONDO-white text-EXPONDO-black font-normal text-left mt-3 shadow-2xl text-sm border ">
        <div class="flex flex-col gap-y-0.5 w-full text-left px-4 py-2 border-b bg-EXPONDO-secondary-light select-none">
          <div class="text-base text-EXPONDO-black font-bold truncate">
            {{ firstName }} {{ lastName }}
          </div>
          <div class="text-sm text-EXPONDO-grey-80 italic font-normal truncate">
            {{ emailAddress }}
          </div>
        </div>
        <PartnersSummary
          v-if="canShowLoyaltyLevelLabel"
          class="px-4 py-3 border-b"
        />
        <div
          v-for="url in urlList"
          :key="url.key.value"
        >
          <MyAccountDropdownItem
            :url="url"
            @click="changePage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">

import { userGetters } from '~/utils/ct';
import PartnersSummary from '~/components/molecules/PartnersSummary/PartnersSummary.vue';

import type { UseMyAccountPage } from '~~/types/myaccount/UseMyAccountPage';

export default defineComponent({
  name: 'MyAccountDropdown',
  components: {
    PartnersSummary,
  },
  emits: ['handleAccountClick', 'hideNavigation'],

  setup(_props, { emit }) {

    const isOpen = ref(false);
    const dropdownAnchor = ref<HTMLElement | null>(null);
    const panelLeftOffset = ref(0);
    const panelRightOffset = ref(0);
    const { load: loadLoyaltyPoints } = useLoyaltyPoints();

    const getContactButtonLeft = (anchor: HTMLElement, anchorLeft: number): number => {

      const contactButtonLeft = anchor.previousElementSibling?.getBoundingClientRect().left;

      return contactButtonLeft || anchorLeft;

    };

    const alignPanelWithHeaderIcons = () => {

      const anchor = dropdownAnchor.value;
      const headerIcons = anchor?.parentElement;

      if (!anchor || !headerIcons) {

        return;

      }

      const anchorRect = anchor.getBoundingClientRect();

      panelLeftOffset.value = getContactButtonLeft(anchor, anchorRect.left) - anchorRect.left;
      panelRightOffset.value = anchorRect.right - headerIcons.getBoundingClientRect().right;

    };

    const open = () => {

      alignPanelWithHeaderIcons();
      isOpen.value = true;

      if (canShowLoyaltyLevelLabel.value) {

        loadLoyaltyPoints();

      }

    };
    const close = () => {

      isOpen.value = false; 

    };

    const { urlList } = useMyAccount();
    const { user } = useUserExtended();
    const { isLoyaltyDiscountEnabled } = useStoreVsfExtended();

    const firstName = computed(() => userGetters.getFirstName(user.value));
    const lastName = computed(() => userGetters.getLastName(user.value));
    const emailAddress = computed(() => userGetters.getEmailAddress(user.value));
    const canShowLoyaltyLevelLabel = computed(() => isLoyaltyDiscountEnabled.value && !!emailAddress.value);

    const changePage = (page: ReturnType<UseMyAccountPage>) => {

      isOpen.value = false;
      emit('hideNavigation');
      page.goToThatPage();
    
    };

    const handleAccountClick = (): void => {

      isOpen.value = false;
      emit('handleAccountClick');
    
    };

    return {
      dropdownAnchor,
      panelLeftOffset,
      panelRightOffset,
      open,
      close,
      isOpen,
      handleAccountClick,
      changePage,
      urlList,
      firstName,
      lastName,
      emailAddress,
      canShowLoyaltyLevelLabel,
    };
  
  },
});

</script>
